import type { CustomReportProps } from '..'
import { Card } from '../shared/Card'
import { displayText } from '../shared/format'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

import { formatDateTime, weekLabel } from './format'
import { FrozenTrendChart, type FrozenStackRow } from './FrozenTrendChart'

const ALL = '(all)'

const WAIVER_SPECS = [
  { value: 'brix', low: 'brix_low', high: 'brix_high', target: 'brix_target', label: 'Brix' },
  { value: 'acid', low: 'acid_low', high: 'acid_high', target: 'acid_target', label: 'Acid' },
  { value: 'ph', low: 'ph_low', high: 'ph_high', target: 'ph_target', label: 'pH' },
  { value: 'oil', low: 'oil_low', high: 'oil_high', target: 'oil_target', label: 'Oil' },
  { value: 'ratio', low: 'ratio_low', high: 'ratio_high', target: 'ratio_target', label: 'B-A-R' },
  {
    value: 'suspended_pulp',
    low: 'pulp_low',
    high: 'pulp_high',
    target: null,
    label: 'Suspended Pulp',
  },
] as const

export function IncomingProduct({ result, values, setParam }: CustomReportProps) {
  const frozenTotals = dataset(result, 'weekly_totals')
  const frozenDetail = dataset(result, 'primary')
  const overTempTotals = dataset(result, 'over_temp_totals')
  const rejectedTotals = dataset(result, 'rejected_totals')
  const rejectedDetail = dataset(result, 'rejected_detail')
  const waiverTotals = dataset(result, 'waiver_totals')
  const waiverDetail = dataset(result, 'waiver_detail')
  const rejectedSpecDetail = dataset(result, 'rejected_spec_detail')
  const selectedWeek = text(dataset(result, 'selection')[0]?.week ?? null)
  const site = typeof values.site === 'string' && values.site !== ALL ? values.site : 'all sites'
  const product =
    typeof values.product === 'string' && values.product !== ALL ? values.product : 'all products'
  const frozenShipments = frozenTotals.reduce((sum, row) => sum + count(row.shipments), 0)
  const overTempShipments = overTempTotals.reduce((sum, row) => sum + count(row.shipments), 0)
  const rejectedShipments = rejectedTotals.reduce((sum, row) => sum + count(row.shipments), 0)
  const waiverShipments = waiverTotals.reduce((sum, row) => sum + count(row.shipments), 0)

  const chartRows = (
    key: 'weekly_frozen' | 'weekly_rejected' | 'weekly_over_temp',
  ): FrozenStackRow[] =>
    dataset(result, key).map((row) => ({
      week: text(row.week) ?? '',
      itemKey: text(row.item_key) ?? '',
      itemName: text(row.item_name) ?? 'Unknown item',
      shipments: count(row.shipments),
    }))

  const frozenSubtitle =
    selectedWeek === null
      ? 'No selected week'
      : `Week of ${weekLabel(selectedWeek)} - ${frozenDetail.length.toString()} frozen ${
          frozenDetail.length === 1 ? 'shipment' : 'shipments'
        }`
  const rejectedSubtitle =
    selectedWeek === null
      ? 'No selected week'
      : `Week of ${weekLabel(selectedWeek)} - ${rejectedDetail.length.toString()} rejected ${
          rejectedDetail.length === 1 ? 'shipment' : 'shipments'
        }`

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {frozenShipments.toLocaleString()} frozen, {overTempShipments.toLocaleString()} over
        temperature, {rejectedShipments.toLocaleString()} rejected and{' '}
        {waiverShipments.toLocaleString()} waiver{' '}
        {frozenShipments + overTempShipments + rejectedShipments + waiverShipments === 1
          ? 'shipment'
          : 'shipments'}{' '}
        - {site} - {product}
      </p>

      <Card
        title="Frozen tankers by week"
        subtitle="Stacked by item using RCV Receiving Date. Select a week to show the PO numbers and shipment detail below."
      >
        <FrozenTrendChart
          rows={chartRows('weekly_frozen')}
          selectedWeek={selectedWeek}
          noun="frozen shipments"
          emptyLabel="No frozen tankers for these filters."
          onSelectWeek={(week) => {
            setParam('week', week)
          }}
        />
      </Card>

      <Card
        title="Over-temperature tankers by week"
        subtitle={
          'Stacked by item. A tanker counts as over temperature above 45F on arrival. ' +
          'Weeks here use the tanker receiving date; frozen uses the inbound COA RCV ' +
          'Receiving Date, while rejected and waiver use COA Creation Date. This chart ' +
          'is not week-selectable.'
        }
      >
        <FrozenTrendChart
          rows={chartRows('weekly_over_temp')}
          selectedWeek={null}
          noun="over-temperature tankers"
          emptyLabel="No over-temperature tankers for these filters."
        />
      </Card>

      <Card
        title="Rejected shipments by week"
        subtitle="COA Action = Reject, stacked by item. Select a week to show PO-level detail."
      >
        <FrozenTrendChart
          rows={chartRows('weekly_rejected')}
          selectedWeek={selectedWeek}
          noun="rejected shipments"
          emptyLabel="No rejected shipments for these filters."
          onSelectWeek={(week) => {
            setParam('week', week)
          }}
        />
      </Card>

      <ShipmentDetail
        title="Frozen tanker detail"
        subtitle={frozenSubtitle}
        rows={frozenDetail}
        dateLabel="RCV Received"
        dateKey="receiving_date"
        emptyLabel="No frozen tankers for this week."
      />
      <ShipmentDetail
        title="Rejected shipment detail"
        subtitle={rejectedSubtitle}
        rows={rejectedDetail}
        dateLabel="COA Created"
        dateKey="coa_creation_date"
        emptyLabel="No rejected shipments for this week."
      />
      <SpecFailureHeatMap
        rows={rejectedSpecDetail}
        selectedWeek={selectedWeek}
        title="Rejected shipments with failed specs"
        noun="rejected"
        emptyLabel="No rejected shipments for this week."
      />
      <SpecFailureHeatMap
        rows={waiverDetail}
        selectedWeek={selectedWeek}
        title="Waiver shipments with failed specs"
        noun="waiver"
        emptyLabel="No waiver shipments for this week."
      />
    </div>
  )
}

function SpecFailureHeatMap({
  rows,
  selectedWeek,
  title,
  noun,
  emptyLabel,
}: {
  rows: Keyed[]
  selectedWeek: string | null
  title: string
  noun: string
  emptyLabel: string
}) {
  const subtitle =
    selectedWeek === null
      ? 'No selected week'
      : `Week of ${weekLabel(selectedWeek)} - ${rows.length.toString()} ${noun} ${
          rows.length === 1 ? 'shipment' : 'shipments'
        }`

  return (
    <Card
      title={title}
      subtitle={`${subtitle}. Hover a spec cell to see the COA value and original spec range when one exists.`}
    >
      {rows.length === 0 ? (
        <div className="state">{emptyLabel}</div>
      ) : (
        <div className="matrix-scroll waiver-matrix-scroll">
          <table className="waiver-matrix">
            <colgroup>
              <col className="waiver-shipment-col" />
              {WAIVER_SPECS.map((spec) => (
                <col key={spec.value} className="waiver-spec-col" />
              ))}
              <col className="waiver-reason-col" />
            </colgroup>
            <thead>
              <tr>
                <th className="waiver-shipment-head">Shipment</th>
                {WAIVER_SPECS.map((spec) => (
                  <th key={spec.value}>{spec.label}</th>
                ))}
                <th>Detected reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${displayText(text(row.coa_id))}-${displayText(text(row.po_number))}`}>
                  <td className="waiver-shipment-cell">
                    <strong>{displayText(text(row.po_number))}</strong>
                    <span>{formatDateTime(text(row.coa_creation_date))}</span>
                    <span>{displayText(text(row.receiving_location))}</span>
                    <span>{displayText(text(row.item_name))}</span>
                  </td>
                  {WAIVER_SPECS.map((spec) => {
                    const value = numeric(row[spec.value])
                    const failed = isTrue(row[`${spec.value}_failed`])
                    const range = specRange(row, spec)
                    const label = value === null ? '.' : formatSpecNumber(value, spec)
                    return (
                      <td
                        key={spec.value}
                        className={[
                          'waiver-cell',
                          failed ? 'is-failed' : '',
                          range === null ? 'has-no-range' : 'has-range',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={
                          range === null
                            ? undefined
                            : `${spec.label}: COA ${label}; original range ${range}`
                        }
                      >
                        {label}
                      </td>
                    )
                  })}
                  <td className="waiver-reason-cell">
                    {displayText(text(row.detected_waiver_reason))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function ShipmentDetail({
  title,
  subtitle,
  rows,
  dateLabel,
  dateKey,
  emptyLabel,
}: {
  title: string
  subtitle: string
  rows: ReturnType<typeof dataset>
  dateLabel: string
  dateKey: 'receiving_date' | 'coa_creation_date'
  emptyLabel: string
}) {
  return (
    <Card title={title} subtitle={subtitle}>
      {rows.length === 0 ? (
        <div className="state">{emptyLabel}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="detail-table incoming-product-table">
            <thead>
              <tr>
                <th>PO Number</th>
                <th>{dateLabel}</th>
                <th>Location</th>
                <th>Supplier</th>
                <th>Item ID</th>
                <th>Item</th>
                <th>SO Lot #</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${displayText(text(row.coa_id))}-${displayText(text(row.po_number))}`}
                  className="detail-row"
                >
                  <td className="cell-mono">{displayText(text(row.po_number))}</td>
                  <td className="cell-mono">{formatDateTime(text(row[dateKey]))}</td>
                  <td>{displayText(text(row.receiving_location))}</td>
                  <td className="cell-truncate" title={displayText(text(row.supplier_name))}>
                    {displayText(text(row.supplier_name))}
                  </td>
                  <td className="cell-mono">{displayText(text(row.item_id))}</td>
                  <td className="cell-truncate" title={displayText(text(row.item_name))}>
                    {displayText(text(row.item_name))}
                  </td>
                  <td className="cell-mono">{displayText(text(row.so_lot))}</td>
                  <td>{displayText(text(row.coa_status))}</td>
                  <td>{displayText(text(row.coa_action))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function numeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function isTrue(value: unknown): boolean {
  return value === true || String(value).toLowerCase() === 'true'
}

function formatSpecNumber(value: number, spec: (typeof WAIVER_SPECS)[number]): string {
  if (spec.value === 'oil') return value.toFixed(3)
  return Number.isInteger(value) ? value.toString() : value.toFixed(2)
}

function specRange(
  row: Keyed,
  spec: (typeof WAIVER_SPECS)[number],
): string | null {
  const low = numeric(row[spec.low])
  const high = numeric(row[spec.high])
  const target = spec.target === null ? null : numeric(row[spec.target])
  if (low === null && high === null && target === null) return null
  const parts = [
    low === null ? null : `low ${formatSpecNumber(low, spec)}`,
    high === null ? null : `high ${formatSpecNumber(high, spec)}`,
    target === null ? null : `target ${formatSpecNumber(target, spec)}`,
  ].filter((part): part is string => part !== null)
  return parts.join(', ')
}
